/**
 * TanStack Query hook for generating policies from data discovery scan results.
 * Calls POST /api/policy-drafts/generate-from-scan and invalidates policy caches on success.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { post } from '@/lib/api-client'
import { policyKeys } from '@/hooks/use-policies'

interface GeneratePoliciesDraft {
  id: string
  name: string
  description: string | null
  status: string
}

interface GeneratePoliciesResponse {
  drafts: GeneratePoliciesDraft[]
  count: number
}

/**
 * Hook to auto-generate XACML policy drafts from a completed data discovery scan.
 *
 * @example
 * ```tsx
 * const generatePolicies = useGeneratePolicies()
 *
 * const handleGenerate = () => {
 *   generatePolicies.mutate(scanId, {
 *     onSuccess: (data) => {
 *       toast.success(`Generated ${data.count} policies`)
 *     },
 *   })
 * }
 * ```
 */
export function useGeneratePolicies() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scanId: string) => {
      return post<GeneratePoliciesResponse, { scanId: string }>(
        '/policy-drafts/generate-from-scan',
        { scanId }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.lists() })
    },
  })
}
